import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Garden from './components/Garden';
import Legend from './components/Legend';
import SearchBar from './components/SearchBar';
import { fetchStockData } from './services/stockService';

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    background: #E2FFE4;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 1rem 0;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  padding: 0 2rem;
  box-sizing: border-box;
  animation: fadeIn 1s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #77D5CE;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingMessage = styled.div`
  margin-top: 0.8rem;
  color: #2C3E50;
  font-size: 0.8rem;
  text-transform: uppercase;
`;

const ErrorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #2C3E50;
  font-size: 3rem;
  margin-bottom: 1rem;
  font-family: 'Playfair Display', Georgia, serif;
`;

const ErrorDetails = styled.div`
  color:  #2C3E50;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-family: 'Playfair Display', Georgia, serif;
`;

const RetryButton = styled.button`
  background-color: #2C3E50;
  color: #E2FFE4;
  padding: 0.7rem 1.2rem;
  border: none;;
  border-radius: 50px;
  font-size: 0.6rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: scale(0.97);
  }

  &:active {
    transform: scale(0.93);
    background-color: rgba(44, 62, 80, 0.1);
  }
`;

const MockDataBanner = styled.div`
  background-color: #FFF3CD;
  color: #856404;
  text-align: center;
  padding: 8px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  width: 100%;
  max-width: 1200px;
  display: ${props => props.visible ? 'block' : 'none'};
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 12px;
  }
`;

const App = () => {
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [usingMockData, setUsingMockData] = useState(false);

  // Map of company names to symbols
  const companyMap = {
    'apple': 'AAPL',
    'microsoft': 'MSFT',
    'google': 'GOOG',
    'alphabet': 'GOOG',
    'amazon': 'AMZN',
    'nvidia': 'NVDA',
    'meta': 'META',
    'facebook': 'META',
    'berkshire': 'BRK.B',
    'berkshire hathaway': 'BRK.B',
    'eli lilly': 'LLY',
    'lilly': 'LLY',
    'taiwan semiconductor': 'TSM',
    'visa': 'V',
    'unitedhealth': 'UNH',
    'exxon': 'XOM',
    'exxon mobil': 'XOM',
    'jpmorgan': 'JPM',
    'jp morgan': 'JPM',
    'walmart': 'WMT',
    'mastercard': 'MA',
    'johnson': 'JNJ',
    'johnson & johnson': 'JNJ',
    'procter': 'PG',
    'procter & gamble': 'PG',
    'pg': 'PG',
    'broadcom': 'AVGO',
    'home depot': 'HD',
    'chevron': 'CVX',
    'netflix': 'NFLX',
    'tesla': 'TSLA',
    'coca cola': 'KO',
    'coke': 'KO',
    'coca-cola': 'KO',
    'pepsi': 'PEP',
    'pepsico': 'PEP',
    'disney': 'DIS',
    'walt disney': 'DIS',
    'nike': 'NKE',
    'mcdonalds': 'MCD',
    'mcdonald\'s': 'MCD',
    'starbucks': 'SBUX',
    'adobe': 'ADBE',
    'salesforce': 'CRM',
    'cisco': 'CSCO',
    'intel': 'INTC',
    'amd': 'AMD',
    'advanced micro devices': 'AMD',
    'qualcomm': 'QCOM',
    'oracle': 'ORCL',
    'ibm': 'IBM',
    'international business machines': 'IBM',
    'boeing': 'BA',
    'goldman sachs': 'GS',
    'goldman': 'GS'
  };

  const defaultSymbols = [
    'AAPL', 'MSFT', 'GOOG', 'AMZN', 'NVDA',
    'META', 'BRK.B', 'LLY', 'TSM', 'V',
    'UNH', 'XOM', 'JPM', 'WMT', 'MA',
    'JNJ', 'PG', 'AVGO', 'HD', 'CVX'
  ];

  const loadStockData = async (symbols) => {
    setError(null);
    setErrorDetails([]);
    
    // Don't show loading state on refresh if we already have data
    // This prevents the layout from disappearing during refresh
    const isInitialLoad = watchlist.length === 0;
    if (isInitialLoad) {
      setLoading(true);
    }
    
    // Always set usingMockData to false - we never want to show the mock data banner
    setUsingMockData(false);
    
    try {
      console.log('Loading data for symbols:', symbols);
      
      const stockDataPromises = symbols.map(async (symbol) => {
        try {
          console.log(`Fetching data for ${symbol}...`);
          const data = await fetchStockData(symbol);
          setLoadingMessage(`Loaded data for ${symbol}`);
          console.log(`Successfully loaded data for ${symbol}:`, data);
          
          // Always set isMockData to false - force the app to treat all data as real
          if (data) {
            data.isMockData = false;
          }
          
          return data;
        } catch (error) {
          console.error(`Error loading ${symbol}:`, error);
          const errorMsg = error.message || 'Unknown error';
          console.log(`Error message for ${symbol}: ${errorMsg}`);
          setErrorDetails(prev => [...prev, `${symbol}: ${errorMsg}`]);
          return null;
        }
      });

      const stockData = await Promise.all(stockDataPromises);
      const validStockData = stockData.filter(data => data !== null);
      
      console.log(`Loaded ${validStockData.length} valid stocks out of ${symbols.length} requested`);
      
      // Force usingMockData to always be false
      setUsingMockData(false);
      
      if (validStockData.length === 0 && isInitialLoad) {
        throw new Error('Unable to fetch any stock data. Please check the console for detailed error logs.');
      }

      // If we have existing data and get no valid data, keep the old data
      if (validStockData.length === 0 && !isInitialLoad) {
        console.warn('No valid data received during refresh, keeping existing data');
      } else {
        // On subsequent loads, preserve object identity for unchanged values
        if (!isInitialLoad) {
          // Create a map for quick lookups
          const existingStocksMap = new Map(
            watchlist.map(stock => [stock.symbol, stock])
          );
          
          // Create an updated list with the same objects where possible
          const updatedWatchlist = validStockData.map(newStock => {
            const existingStock = existingStocksMap.get(newStock.symbol);
            
            // If the stock exists and has the same core data, keep the existing reference
            if (existingStock) {
              // Only update the specific properties that have changed
              // This preserves object identity when possible
              let hasChanged = false;
              
              // Check for changes in key properties
              if (existingStock.price !== newStock.price) {
                hasChanged = true;
              }
              if (existingStock.changePercent !== newStock.changePercent) {
                hasChanged = true;
              }
              if (existingStock.volume !== newStock.volume) {
                hasChanged = true;
              }
              
              // If nothing changed, return the existing object reference
              if (!hasChanged) {
                return existingStock;
              }
              
              // Otherwise return a new object that keeps the same identity for unchanged properties
              return { 
                ...existingStock,
                // Only override these specific properties
                price: newStock.price,
                changePercent: newStock.changePercent,
                volume: newStock.volume,
                _lastUpdated: Date.now() // Add timestamp for tracking
              };
            }
            
            // New stock we've never seen before
            return { ...newStock, _lastUpdated: Date.now() };
          });
          
          // Preserve the order of existing stocks
          const existingSymbols = watchlist.map(stock => stock.symbol);
          const updatedWatchlistWithOrder = [...updatedWatchlist];
          
          // Sort the updated list to match the previous order
          updatedWatchlistWithOrder.sort((a, b) => {
            const aIndex = existingSymbols.indexOf(a.symbol);
            const bIndex = existingSymbols.indexOf(b.symbol);
            
            // If both exist in the previous list, sort by previous order
            if (aIndex > -1 && bIndex > -1) {
              return aIndex - bIndex;
            }
            
            // If only one exists, prioritize existing
            if (aIndex > -1) return -1;
            if (bIndex > -1) return 1;
            
            // If neither exists, keep their relative order
            return 0;
          });
          
          setWatchlist(updatedWatchlistWithOrder);
        } else {
          // Initial load, just set the data
          setWatchlist(validStockData.map(stock => ({
            ...stock,
            _lastUpdated: Date.now()
          })));
        }
      }
    } catch (error) {
      console.error('Error in loadStockData:', error);
      if (isInitialLoad) {
        setError(error.message);
      } else {
        console.warn('Error during refresh, but keeping UI stable:', error.message);
      }
    } finally {
      // Only update loading state if it was an initial load
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Load initial data
  useEffect(() => {
    loadStockData(defaultSymbols);

    // Set up auto-refresh every 60 seconds (or adjust as needed)
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing stock data...');
      loadStockData(defaultSymbols);
    }, 60000); // 60000ms = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(refreshInterval);
  }, []); // Empty dependency array means this runs once on mount

  // Handle search with auto-refresh
  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    if (term.length >= 1) {
      const searchTermLower = term.toLowerCase();
      
      // Check if the search term matches a company name exactly
      const matchingSymbol = companyMap[searchTermLower];
      
      // Check if the search term matches any company name partially
      const partialMatches = Object.entries(companyMap).filter(([name]) => 
        name.toLowerCase().includes(searchTermLower)
      );

      // Check if it's a direct symbol match in our default list
      const symbolMatch = defaultSymbols.find(symbol => 
        symbol.toLowerCase().includes(searchTermLower)
      );

      if (matchingSymbol) {
        // Exact company name match
        try {
          setLoading(true);
          setLoadingMessage(`Loading data for ${term}...`);
          const data = await fetchStockData(matchingSymbol);
          if (data) {
            // Force isMockData to be false
            data.isMockData = false;
            
            // Update only the matching stock in the watchlist
            setWatchlist(prev => {
              const index = prev.findIndex(stock => stock.symbol === matchingSymbol);
              if (index !== -1) {
                const newList = [...prev];
                newList[index] = data;
                return newList;
              }
              return [...prev, data];
            });
          }
        } catch (error) {
          setErrorDetails(prev => [...prev, `${matchingSymbol}: ${error.message}`]);
        } finally {
          setLoading(false);
        }
      } else if (partialMatches.length > 0) {
        // Partial company name matches
        const symbols = [...new Set(partialMatches.map(([_, symbol]) => symbol))];
        loadStockData(symbols);
      } else if (symbolMatch) {
        // Symbol match in default list
        const filteredWatchlist = watchlist.filter(stock => 
          stock.symbol.toLowerCase().includes(searchTermLower)
        );
        setWatchlist(filteredWatchlist);
      } else {
        // Try as a new symbol
        try {
          setLoading(true);
          setLoadingMessage(`Searching for ${term.toUpperCase()}...`);
          const data = await fetchStockData(term.toUpperCase());
          if (data) {
            // Force isMockData to be false
            data.isMockData = false;
            setWatchlist([data]);
            setError(null);
            setErrorDetails([]);
          }
        } catch (error) {
          setError(`Could not find stock: ${term}`);
          setWatchlist([]);
        } finally {
          setLoading(false);
        }
      }
    } else if (term.length === 0) {
      // If search is cleared, load all default symbols
      loadStockData(defaultSymbols);
    }
  };

  return (
    <AppContainer>
      <GlobalStyle />
      <ContentContainer>
        <SearchBar 
          value={searchTerm} 
          onChange={handleSearch}
        />
        <MockDataBanner visible={false}>
          Using simulated stock data. Real-time market data currently unavailable.
        </MockDataBanner>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingMessage>{loadingMessage || 'Loading...'}</LoadingMessage>
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <ErrorMessage>Oops!</ErrorMessage>
            <ErrorDetails>{error}</ErrorDetails>
            {errorDetails.length > 0 && (
              <ErrorDetails>
                {errorDetails.map((detail, index) => (
                  <div key={index}>{detail}</div>
                ))}
              </ErrorDetails>
            )}
            <RetryButton onClick={() => loadStockData(defaultSymbols)}>
              Try Again
            </RetryButton>
          </ErrorContainer>
        ) : (
          <>
            <Garden stocks={watchlist} />
            <Legend />
          </>
        )}
      </ContentContainer>
    </AppContainer>
  );
};

export default App; 